import React from "react";
import { Container, Row } from "react-grid-system";
import { withTranslation } from "react-i18next";

import styles from "./Editor.module.scss";
import history from "../../history";
import httpClient from "../../lib/HttpClient";

import notificationService from "../../services/Notifications";

/* UI Kit */
import "@uik/styles.css";
import "../../font.scss";

/* Papercurve Components */
import Header from "../shared/Header/Header";

const EDITOR_FRAME_NAME = "papercurve-office-editor";

// How long to wait for a save to show up in the API before reporting a problem.
const SAVE_CONFIRM_TIMEOUT_MS = 20000;

// Notifications normally appear top right, which here is on top of the
// editor's Exit button, and they stay while the pointer is over them.
const NOTIFICATION_POSITION = "top-center";

// Collabora's starting layout: light theme (it otherwise follows the OS dark
// mode setting), the single-row toolbar, and no side panel or ruler.
// SavedUIState=false makes every session start this way; without it Collabora
// restores whatever layout the user last left in this browser.
const EDITOR_UI_DEFAULTS = [
  "SavedUIState=false",
  "UITheme=light",
  "UIMode=compact",
  "TextSidebar=false",
  "TextRuler=false",
  "SpreadsheetSidebar=false",
  "PresentationSidebar=false",
  "DrawingSidebar=false",
].join(";");

class EditorBuild extends React.Component {
  constructor() {
    super();

    this.sessionForm = React.createRef();
    this.editorFrame = React.createRef();
    // Set when the user closes the editor with unsaved changes: the editor is
    // left open until that save has reached Papercurve.
    this.closingAfterSave = false;
    // What the API last reported as stored for this document. A save has
    // landed once the API reports something different.
    this.revision = null;
    this.latestDocumentId = null;
    this.savePoll = null;
    this.state = {
      session: null,
      error: null,
    };
  }

  componentDidMount = () => {
    const docId = this.props.match.params.id;

    if (document.getElementById("root")) {
      document.getElementById("root").className = styles.fullHeight;
    }

    if (document.getElementById("app_container")) {
      document.getElementById("app_container").className = styles.fullHeight;
    }

    window.addEventListener("message", this.onEditorMessage);
    this.openSession(docId);
  };

  componentWillUnmount = () => {
    window.removeEventListener("message", this.onEditorMessage);
    clearTimeout(this.savePoll);
  };

  openSession = (docId) => {
    httpClient
      .post(`/documents/${docId}/edit_session.json`)
      .then((response) => {
        this.revision = response.data.revision;
        this.latestDocumentId = Number(docId);

        // The editor is loaded by posting the access token into the iframe,
        // which keeps the token out of the iframe's URL.
        this.setState({ session: response.data }, () => {
          if (this.sessionForm.current) {
            this.sessionForm.current.submit();
          }
        });
      })
      .catch((error) => {
        const data = error.response && error.response.data;

        // Only the latest version can be edited, so move on to it.
        if (error.response && error.response.status === 409) {
          history.replace(`/editor/${data.latest_document_id}`);
          this.openSession(data.latest_document_id);
          return;
        }

        this.setState({
          error: (data && data.error) || "The editor could not be opened.",
        });
      });
  };

  editorOrigin = () => {
    const { session } = this.state;

    return session ? new URL(session.editor_url).origin : null;
  };

  postToEditor = (messageId, values) => {
    const frame = this.editorFrame.current;

    if (this.editorOrigin() && frame && frame.contentWindow) {
      frame.contentWindow.postMessage(
        JSON.stringify({ MessageId: messageId, Values: values }),
        this.editorOrigin()
      );
    }
  };

  // Asks the editor to save and report back with Action_Save_Resp.
  saveNewVersion = () => {
    this.postToEditor("Action_Save", {
      DontTerminateEdit: true,
      DontSaveIfUnmodified: true,
      Notify: true,
    });
  };

  onEditorMessage = (event) => {
    let message;

    if (!this.editorOrigin() || event.origin !== this.editorOrigin()) {
      return;
    }

    try {
      message = JSON.parse(event.data);
    } catch (e) {
      return;
    }

    const values = message.Values || {};

    switch (message.MessageId) {
      case "App_LoadingStatus":
        if (values.Status === "Frame_Ready") {
          // The editor ignores every other message until it gets this one.
          this.postToEditor("Host_PostmessageReady");
        } else if (values.Status === "Document_Loaded") {
          // Saving and closing are handled here so the user can be told when a
          // new version exists, and is only returned to it once it does.
          this.postToEditor("Disable_Default_UIAction", {
            action: "UI_Save",
            disable: true,
          });
          this.postToEditor("Disable_Default_UIAction", {
            action: "UI_Close",
            disable: true,
          });
        }
        break;
      case "UI_Save":
        this.saveNewVersion();
        break;
      case "UI_Close":
        this.closingAfterSave = true;
        this.saveNewVersion();
        break;
      case "Action_Save_Resp":
        this.onSaveResult(values);
        break;
      default:
        break;
    }
  };

  onSaveResult = (result) => {
    const unmodified = result.result === "unmodified";

    if (!result.success && !unmodified) {
      this.saveNotConfirmed();
      return;
    }

    if (unmodified) {
      if (this.closingAfterSave) {
        this.closeEditor();
      }
      return;
    }

    // The editor reports success once it has written the file on its own
    // server, which is before it has sent the file to Papercurve. Wait for the
    // API to show the save before telling the user or leaving the editor.
    clearTimeout(this.savePoll);
    this.awaitSave(Date.now() + SAVE_CONFIRM_TIMEOUT_MS);
  };

  awaitSave = (deadline) => {
    const retry = () => {
      if (Date.now() > deadline) {
        this.saveNotConfirmed();
        return;
      }

      this.savePoll = setTimeout(() => this.awaitSave(deadline), 500);
    };

    httpClient
      .get(`/documents/${this.props.match.params.id}/edit_status.json`)
      .then((response) => {
        if (response.data.revision === this.revision) {
          retry();
          return;
        }

        const newVersion =
          response.data.latest_document_id !== this.latestDocumentId;

        this.revision = response.data.revision;
        this.latestDocumentId = response.data.latest_document_id;

        notificationService.addNotification(
          newVersion ? "New version saved" : "Saved",
          newVersion
            ? "Your changes were saved as a new version."
            : "Your changes were saved.",
          "success",
          NOTIFICATION_POSITION
        );

        if (this.closingAfterSave) {
          history.push(`/drafts/${this.latestDocumentId}`);
        }
      })
      .catch(retry);
  };

  saveNotConfirmed = () => {
    this.closingAfterSave = false;
    notificationService.addNotification(
      "Changes not saved",
      "Your changes could not be saved. The editor is still open.",
      "warning",
      NOTIFICATION_POSITION
    );
  };

  closeEditor = () => {
    history.push(`/drafts/${this.props.match.params.id}?latest=true`);
  };

  render() {
    const { session, error } = this.state;

    return (
      <Container fluid className={styles.fullHeight}>
        <Header />
        <Row className={styles.editorContainer}>
          {error && <div className={styles.editorError}>{error}</div>}
          {session && (
            <form
              ref={this.sessionForm}
              action={session.editor_url}
              method="post"
              target={EDITOR_FRAME_NAME}
              className={styles.sessionForm}
            >
              <input
                type="hidden"
                name="access_token"
                value={session.access_token}
              />
              <input
                type="hidden"
                name="access_token_ttl"
                value={session.access_token_ttl}
              />
              <input
                type="hidden"
                name="ui_defaults"
                value={EDITOR_UI_DEFAULTS}
              />
            </form>
          )}
          {!error && (
            <iframe
              ref={this.editorFrame}
              name={EDITOR_FRAME_NAME}
              title="Document editor"
              className={styles.editorFrame}
              allow="clipboard-read; clipboard-write"
              allowFullScreen
            />
          )}
        </Row>
      </Container>
    );
  }
}

const Editor = withTranslation()(EditorBuild);

export default Editor;
